/**
 * Meta credentials form — the flow's own cross-package types, and the one
 * derivation the form itself cannot do inline: which of its fields the save
 * contract refuses.
 *
 * `MetaSaveIntent` IS the wire contract's own metaAds variant minus the
 * discriminant (saveIntegration.action.ts), never a parallel shape that
 * agrees with it today: the form assembles exactly what travels, so a field
 * added to or renamed on that variant is a compile error here rather than a
 * value the form silently stops sending. The form dispatches it as a `save`
 * CustomEvent and the mounting card
 * (web/src/admin/settings/integrations/meta/meta-integration.view.ts)
 * forwards it into saveIntegrationAction$, which this package cannot import.
 *
 * Every format this form checks — the pixel's digit count, the ad account's
 * `act_` prefix, the app/page/Instagram numeric strings, a credential's
 * no-whitespace paste guard — is stated once, in the contract and the
 * registry, and read from there by parsing. No regex is restated here: a
 * second statement of a format is free to disagree with the one the server
 * actually enforces, and the operator meets that disagreement as a refused
 * save with no field named.
 */
import { SaveIntegration } from "@momo/types";

/** What one save carries, minus the id the card supplies. */
export type MetaSaveIntent = Omit<
	Extract<SaveIntegration.SaveIntegrationInput, { id: "metaAds" }>,
	"id" | "orgId"
>;

/** Every field the operator fills — the two credentials nested under
 *  `secrets`, and the six operational fields beside them. */
export type MetaField =
	| keyof MetaSaveIntent["secrets"]
	| Exclude<keyof MetaSaveIntent, "secrets">;

/** The seven this form actually offers. The contract's eighth, `browserTag`,
 *  has no control here and never travels on a save. */
export type MetaTextField = Exclude<MetaField, "browserTag">;

/**
 * Which fields the save contract refuses, by parsing the intent the form is
 * about to send against that contract itself. Exhaustive by annotation: a
 * field added to the metaAds variant is a missing-property compile error
 * here, so a new field cannot reach the form unchecked. An omitted field
 * raises no issue — omission is the contract's "keep whatever is stored".
 */
export function malformedMetaFields(
	intent: MetaSaveIntent,
): Readonly<Record<MetaField, boolean>> {
	const issues =
		SaveIntegration.MetaAdsInputSchema.omit({ orgId: true }).safeParse({
			id: "metaAds",
			...intent,
		}).error?.issues ?? [];
	// A credential's issue path is ["secrets", field]; every other field's is
	// [field] — one predicate reads both.
	const malformed = (field: MetaField): boolean =>
		issues.some((issue) =>
			issue.path[0] === "secrets"
				? issue.path[1] === field
				: issue.path[0] === field,
		);
	return {
		accessToken: malformed("accessToken"),
		appSecret: malformed("appSecret"),
		pixelId: malformed("pixelId"),
		browserTag: malformed("browserTag"),
		appId: malformed("appId"),
		adAccountId: malformed("adAccountId"),
		pageId: malformed("pageId"),
		instagramAccountId: malformed("instagramAccountId"),
	};
}
